/**
 * OrderDetailDrawer · Feb 2026 · Iter 204
 * ─────────────────────────────────────────────────────────────────
 * Linear/Superhuman-style side drawer for wholesale dropship order
 * detail. Opens on row click, closes on Esc, supports ↑/↓ keyboard
 * navigation between adjacent orders in the current filtered list
 * WITHOUT losing table context (filters, scroll, selection).
 *
 * Sections rendered (in order):
 *   1. Header · code · status timeline · tags · external IDs
 *   2. Customer + Ship-to (structured address · phone if any)
 *   3. Item(s) · product · SKU · qty · thumbnail (future)
 *   4. Wholesale + Retail split with computed margin %
 *   5. Fulfillment · tracking · carrier · weight · insurance status
 *   6. Timeline · created → forwarded → packed → shipped → delivered
 *   7. Actions · Ship-with-Verqo · Manual · Mark delivered · SLA
 *      request · Void label · Reprint · Split · Tag · Add note
 *
 * Wholesale-specific vs. Shopify retail:
 *   · Shows Buyer identity + Buyer # (external retailer order)
 *   · Wholesale vs Retail split with margin %
 *   · Verqo insurance status per label
 *   · Buyer-term callout (per-buyer pricing rule, credit terms)
 *
 * Phase 1 MVP: renders from existing order data on the row (no new
 * backend call). Buyer terms + full invoice/dispute panels queued
 * for Phase 2. See CHANGELOG iter 204.
 */
import { useEffect, useMemo } from "react";
import api, { money } from "../../../../lib/api";
import { toast } from "sonner";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "../../../../components/ui/sheet";
import {
  ChevronUp, ChevronDown, X, MapPin, Package, Truck, Clock,
  DollarSign, Check, AlertTriangle, Building2, ShieldCheck,
  Copy, Send, ExternalLink, ShoppingBag,
} from "lucide-react";

/** Format an ISO timestamp for the compact timeline pill. */
function fmt(ts) {
  if (!ts) return "—";
  const d = new Date(ts);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-US", {
    month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
  });
}

/** Compute margin % from wholesale + retail; returns null when either is missing. */
function marginPct(wholesale, retail) {
  const w = Number(wholesale || 0);
  const r = Number(retail || 0);
  if (!w || !r || r <= w) return null;
  return Math.round(((r - w) / r) * 100);
}

/** Copy-to-clipboard helper with toast confirmation. */
function copyText(label, value) {
  if (!value) return;
  navigator.clipboard?.writeText(String(value));
  toast.success(`${label} copied`);
}

export default function OrderDetailDrawer({
  order,
  siblings,       // array — filtered order list, for ↑/↓ nav
  onClose,
  onNavigate,     // (nextOrder) => void
  onAction,       // { ship, manualTrack, deliver, slaRequest, tag, split }
}) {
  // Compute the position of the current order in the filtered list so we
  // can render "3 / 11" and enable prev/next buttons only when applicable.
  const { index, total, prev, next } = useMemo(() => {
    if (!order || !Array.isArray(siblings)) return { index: -1, total: 0, prev: null, next: null };
    const i = siblings.findIndex(o => o.id === order.id);
    return {
      index: i,
      total: siblings.length,
      prev: i > 0 ? siblings[i - 1] : null,
      next: i >= 0 && i < siblings.length - 1 ? siblings[i + 1] : null,
    };
  }, [order, siblings]);

  // Keyboard nav · Esc closes, ↑/↓ move between orders. Only bound when
  // the drawer is actually open — otherwise we'd hijack list-level keys.
  useEffect(() => {
    if (!order) return undefined;
    const handler = (e) => {
      // Ignore when the user is typing in an input inside the drawer.
      const tag = e.target?.tagName;
      if (["INPUT", "TEXTAREA", "SELECT"].includes(tag) || e.target?.isContentEditable) return;
      if (e.key === "Escape") { onClose?.(); }
      else if (e.key === "ArrowUp"   && prev) { e.preventDefault(); onNavigate?.(prev); }
      else if (e.key === "ArrowDown" && next) { e.preventDefault(); onNavigate?.(next); }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [order, prev, next, onClose, onNavigate]);

  if (!order) return null;

  const wholesaleTotal = Number(order.wholesale_cost || 0) * Number(order.qty || 0);
  const retailTotal    = Number(order.retail_price   || 0) * Number(order.qty || 0);
  const margin         = marginPct(order.wholesale_cost, order.retail_price);
  const isForwarded    = order.status === "forwarded";
  const isShipped      = ["shipped", "in_transit"].includes(order.status);
  const isDelivered    = order.status === "delivered";
  const isCancelled    = order.status === "cancelled";
  const mv             = order.movement_state;
  const slaExt         = order.sla_extension;

  return (
    <Sheet open={!!order} onOpenChange={(open) => !open && onClose?.()}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-[720px] p-0 flex flex-col overflow-hidden"
        data-testid="order-detail-drawer"
      >
        {/* ── Sticky drawer header · nav + close ───────────────────── */}
        <SheetHeader className="px-6 py-3 border-b border-border flex flex-row items-center gap-2 space-y-0 shrink-0">
          <button
            onClick={() => prev && onNavigate?.(prev)}
            disabled={!prev}
            title="Previous order (↑)"
            data-testid="drawer-prev"
            className="w-7 h-7 rounded-md border border-border hover:bg-secondary/60 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center"
          >
            <ChevronUp className="w-4 h-4"/>
          </button>
          <button
            onClick={() => next && onNavigate?.(next)}
            disabled={!next}
            title="Next order (↓)"
            data-testid="drawer-next"
            className="w-7 h-7 rounded-md border border-border hover:bg-secondary/60 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center"
          >
            <ChevronDown className="w-4 h-4"/>
          </button>
          {total > 0 && (
            <span className="text-[11px] mono text-foreground/50 ml-1">
              {index + 1} / {total}
            </span>
          )}
          <SheetTitle className="text-[13.5px] font-semibold ml-3 flex items-center gap-2 min-w-0 flex-1">
            <span className="mono truncate">{order.code}</span>
            <button
              onClick={() => copyText("Order code", order.code)}
              title="Copy order code"
              className="w-5 h-5 rounded hover:bg-secondary/60 grid place-items-center text-foreground/40 hover:text-foreground"
            >
              <Copy className="w-3 h-3"/>
            </button>
            <StatusPill status={order.status} movement={mv} slaStatus={slaExt?.status}/>
          </SheetTitle>
          <button
            onClick={onClose}
            title="Close (Esc)"
            data-testid="drawer-close"
            className="w-7 h-7 rounded-md border border-border hover:bg-secondary/60 flex items-center justify-center"
          >
            <X className="w-4 h-4"/>
          </button>
        </SheetHeader>

        {/* ── Scrollable body · sections stacked vertically ────────── */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-6">
            {/* Section 0 · Order source (Phase-2 · Category A4)
                Only rendered for channel-sourced orders. Shows the
                marketplace, external order ID (copyable), an "Open in
                marketplace" quick link, and the estimated marketplace
                fee so sellers see net revenue at a glance. */}
            {order.source_type === "channel" && (
              <section className="rounded-xl border border-border bg-gradient-to-br from-secondary/40 to-transparent p-3">
                <div className="text-[10.5px] mono uppercase tracking-[0.16em] text-foreground/50 mb-2 flex items-center gap-1.5">
                  <ShoppingBag className="w-3 h-3"/>Direct-sale source
                </div>
                <div className="grid grid-cols-2 gap-2 text-[12px]">
                  <KV k="Channel" v={
                    <span className="inline-flex items-center gap-1.5 font-semibold">
                      {(order.source_channel_type || "").toUpperCase()}
                      <span className="text-foreground/50 font-normal">· {order.source_channel_label}</span>
                    </span>
                  }/>
                  <KV k="External ID" v={order.external_order_id || "—"} copyable/>
                </div>
                {order.marketplace_fee_estimate != null && (
                  <div className="mt-2 text-[11.5px] text-foreground/60">
                    Marketplace fee ≈ <span className="font-semibold text-foreground">{money(order.marketplace_fee_estimate)}</span>
                    {" · "}Net ≈ <span className="font-semibold text-emerald-700">
                      {money((Number(order.retail_price || 0) * Number(order.qty || 0)) - Number(order.marketplace_fee_estimate || 0))}
                    </span>
                  </div>
                )}
                {order.external_marketplace_url && (
                  <a
                    href={order.external_marketplace_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="drawer-marketplace-link"
                    className="mt-2 inline-flex items-center gap-1.5 text-[11.5px] font-medium text-indigo-700 hover:underline"
                  >
                    Open in marketplace <ExternalLink className="w-3 h-3"/>
                  </a>
                )}
              </section>
            )}

            {/* Section 1 · External IDs (retailer-source references) */}
            <Section title="External references" icon={ExternalLink}>
              <KV k="Order code" v={order.code} copyable/>
              <KV k="Buyer order #" v={order.buyer_order_code || order.buyer_code} copyable/>
              <KV k="Source store" v={order.source_store_name || order.source_store || "—"}/>
              <KV k="Placed at" v={fmt(order.created_at || order.placed_at)}/>
            </Section>

            {/* Section 2 · Buyer identity (wholesale-specific) */}
            <Section title="Buyer" icon={Building2}>
              <KV k="Buyer" v={order.buyer_name || "—"}/>
              {order.buyer_terms_note && (
                <div className="mt-2 rounded-lg border border-border bg-secondary/30 px-3 py-2 text-[11.5px]">
                  <span className="text-foreground/50">Terms · </span>
                  <span className="font-medium">{order.buyer_terms_note}</span>
                </div>
              )}
            </Section>

            {/* Section 3 · Customer + structured ship-to */}
            <Section title="Ship to" icon={MapPin}>
              <div className="text-[13px] font-medium">{order.ship_to?.name || order.customer_name}</div>
              {order.ship_to?.street1 ? (
                <div className="text-[12px] text-foreground/70 leading-snug mt-0.5">
                  <div>{order.ship_to.street1}</div>
                  {order.ship_to.street2 && <div>{order.ship_to.street2}</div>}
                  <div className="mono">
                    {order.ship_to.city}, {order.ship_to.state} {order.ship_to.zip}
                  </div>
                  <div className="mono text-foreground/45">{order.ship_to.country || "US"}</div>
                </div>
              ) : (
                <div className="text-[12px] text-amber-700">
                  {order.customer_address || "No structured address — check the source store"}
                </div>
              )}
              {order.ship_to?.phone && (
                <div className="text-[11.5px] mono text-foreground/60 mt-1">☏ {order.ship_to.phone}</div>
              )}
            </Section>

            {/* Section 4 · Item(s) — MVP shows single line-item; grid
                ready for multi-item extension in Phase 2. */}
            <Section title="Item" icon={Package}>
              <div className="rounded-xl border border-border bg-white p-3 flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-secondary/60 grid place-items-center shrink-0">
                  <Package className="w-4 h-4 text-foreground/40"/>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] font-medium truncate">{order.product_name}</div>
                  <div className="text-[11px] mono text-foreground/50">{order.sku || order.product_id}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="mono text-[14px] font-semibold">{order.qty}</div>
                  <div className="text-[10px] mono uppercase tracking-[0.14em] text-foreground/50">qty</div>
                </div>
              </div>
            </Section>

            {/* Section 5 · Wholesale/Retail/Margin split (wholesale-only) */}
            <Section title="Wholesale + Retail" icon={DollarSign}>
              <div className="grid grid-cols-3 gap-2">
                <MoneyTile label="Wholesale"    value={wholesaleTotal}/>
                <MoneyTile label="Retail"       value={retailTotal || null}/>
                <MoneyTile label="Margin %"     value={margin != null ? `${margin}%` : null} tone="emerald"/>
              </div>
              <div className="text-[11px] text-foreground/50 mt-2">
                Wholesale cost {money(order.wholesale_cost || 0)} × {order.qty} qty
                {order.retail_price ? ` · Retail ${money(order.retail_price)} × ${order.qty}` : ""}
              </div>
            </Section>

            {/* Section 6 · Fulfillment · tracking + insurance */}
            <Section title="Fulfillment" icon={Truck}>
              <KV k="Status"        v={<StatusPill status={order.status} movement={mv} slaStatus={slaExt?.status}/>}/>
              <KV k="Tracking"      v={order.tracking ? (
                <button onClick={() => copyText("Tracking", order.tracking)} className="mono hover:underline">
                  {order.tracking} <Copy className="w-2.5 h-2.5 inline opacity-50"/>
                </button>
              ) : "—"} />
              <KV k="Carrier"       v={order.carrier || (order.tracking ? "Auto-detected" : "—")}/>
              <KV k="Weight"        v={order.weight_lb ? `${order.weight_lb} lb` : "—"}/>
              <KV k="Verqo insured" v={order.label_type === "verqo" ? (
                <span className="inline-flex items-center gap-1 text-emerald-700"><ShieldCheck className="w-3 h-3"/>Yes</span>
              ) : (order.tracking ? <span className="text-foreground/50">Manual — no insurance</span> : "—")}/>
              {mv && mv !== "moving" && (
                <div className={`mt-2 rounded-lg border px-3 py-2 text-[11.5px] flex items-center gap-2 ${
                  mv === "no_movement" ? "bg-rose-50 border-rose-200 text-rose-700" : "bg-amber-50 border-amber-200 text-amber-700"
                }`}>
                  <AlertTriangle className="w-3.5 h-3.5"/>
                  Movement red-flag · {mv === "no_movement" ? "no scans in 96h+" : "stalled 48–96h"}
                  {order.hours_since_move != null && ` · last move ${Math.round(order.hours_since_move)}h ago`}
                </div>
              )}
              {slaExt?.status === "pending" && (
                <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 text-amber-700 px-3 py-2 text-[11.5px] flex items-center gap-2">
                  <Clock className="w-3.5 h-3.5"/>
                  SLA extension pending buyer response · expires in {Math.max(0, Math.round((new Date(slaExt.expires_at) - Date.now()) / 3600000))}h
                </div>
              )}
              {slaExt?.status === "approved" && (
                <div className="mt-2 rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700 px-3 py-2 text-[11.5px] flex items-center gap-2">
                  <Check className="w-3.5 h-3.5"/>
                  Ship-by extended to {new Date(slaExt.committed_ship_by).toLocaleDateString()} by {slaExt.responded_by_name}
                </div>
              )}
            </Section>

            {/* Section 7 · Timeline · lifecycle events (renders even
                without full history — falls back to status-implied dates). */}
            <Section title="Timeline" icon={Clock}>
              <TimelineEvent
                label="Placed"      ts={order.created_at || order.placed_at}      done />
              <TimelineEvent
                label="Forwarded"   ts={order.forwarded_at}                       done={!!order.forwarded_at || order.status !== "new"} />
              <TimelineEvent
                label="Shipped"     ts={order.shipped_at}                         done={isShipped || isDelivered} />
              <TimelineEvent
                label="Delivered"   ts={order.delivered_at}                       done={isDelivered} />
              {isCancelled && (
                <TimelineEvent label="Cancelled" ts={order.cancelled_at} done tone="rose"/>
              )}
            </Section>
          </div>
        </div>

        {/* ── Sticky action bar at the bottom ──────────────────────── */}
        <div className="border-t border-border p-4 shrink-0 flex items-center gap-2 flex-wrap bg-white" data-testid="drawer-action-bar">
          {isForwarded && !order.tracking && (
            <>
              <button
                onClick={() => onAction?.ship?.(order)}
                data-testid="drawer-ship-verqo"
                className="inline-flex items-center gap-1.5 rounded-full h-9 px-4 text-[12px] font-medium bg-foreground text-background hover:bg-foreground/90"
              >
                <ShieldCheck className="w-3.5 h-3.5"/>Ship with Verqo
              </button>
              <button
                onClick={() => onAction?.manualTrack?.(order)}
                data-testid="drawer-manual-track"
                className="inline-flex items-center gap-1.5 rounded-full h-9 px-4 text-[12px] font-medium border border-border bg-white hover:bg-secondary/60"
              >
                <Send className="w-3.5 h-3.5"/>Manual tracking
              </button>
            </>
          )}
          {isShipped && (
            <button
              onClick={() => onAction?.deliver?.(order)}
              data-testid="drawer-mark-delivered"
              className="inline-flex items-center gap-1.5 rounded-full h-9 px-4 text-[12px] font-medium border border-border bg-white hover:bg-secondary/60"
            >
              <Check className="w-3.5 h-3.5"/>Mark delivered
            </button>
          )}
          {(mv === "no_movement" || mv === "stalled") && !slaExt?.status && (
            <button
              onClick={() => onAction?.slaRequest?.(order)}
              data-testid="drawer-sla-request"
              className="inline-flex items-center gap-1.5 rounded-full h-9 px-4 text-[12px] font-medium border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100"
            >
              <Clock className="w-3.5 h-3.5"/>Request SLA extension
            </button>
          )}
          <div className="ml-auto text-[10.5px] mono uppercase tracking-[0.14em] text-foreground/40">
            ↑↓ navigate · Esc close
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

/* ─── local presentation helpers ─────────────────────────────────── */

function Section({ title, icon: Icon, children }) {
  return (
    <section>
      <div className="text-[10.5px] mono uppercase tracking-[0.16em] text-foreground/50 mb-2 flex items-center gap-1.5">
        {Icon && <Icon className="w-3 h-3"/>}
        {title}
      </div>
      <div>{children}</div>
    </section>
  );
}

function KV({ k, v, copyable }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1 text-[12.5px]">
      <span className="text-foreground/55 shrink-0">{k}</span>
      <span className="font-medium text-right truncate">
        {typeof v === "string" && copyable && v !== "—" ? (
          <button onClick={() => copyText(k, v)} className="hover:underline inline-flex items-center gap-1">
            {v}<Copy className="w-2.5 h-2.5 opacity-50"/>
          </button>
        ) : (v || "—")}
      </span>
    </div>
  );
}

function MoneyTile({ label, value, tone }) {
  return (
    <div className="rounded-lg border border-border bg-white px-3 py-2.5">
      <div className="text-[10px] mono uppercase tracking-[0.14em] text-foreground/50">{label}</div>
      <div className={`text-[15px] font-semibold mt-1 tabular-nums ${tone === "emerald" && value ? "text-emerald-700" : ""}`}>
        {value == null ? "—" : (typeof value === "string" ? value : money(value))}
      </div>
    </div>
  );
}

function TimelineEvent({ label, ts, done, tone }) {
  return (
    <div className="flex items-center gap-3 py-1.5">
      <div className={`w-2 h-2 rounded-full shrink-0 ${
        done ? (tone === "rose" ? "bg-rose-500" : "bg-emerald-500") : "bg-foreground/20"
      }`}/>
      <span className={`text-[12.5px] ${done ? "font-medium" : "text-foreground/50"}`}>{label}</span>
      <span className="ml-auto text-[11px] mono text-foreground/50">{fmt(ts)}</span>
    </div>
  );
}

function StatusPill({ status, movement, slaStatus }) {
  const cls =
    slaStatus === "pending"  ? "bg-amber-50 text-amber-700 border-amber-200" :
    slaStatus === "rejected" ? "bg-rose-50 text-rose-700 border-rose-200"    :
    movement === "no_movement" ? "bg-rose-50 text-rose-700 border-rose-200"  :
    movement === "stalled"     ? "bg-amber-50 text-amber-700 border-amber-200" :
    status === "delivered"     ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
    status === "shipped" || status === "in_transit" ? "bg-indigo-50 text-indigo-700 border-indigo-200" :
    "bg-secondary text-foreground/60 border-border";
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] mono uppercase tracking-[0.14em] border ${cls}`}>
      {(slaStatus === "pending" || slaStatus === "rejected" || movement === "no_movement" || movement === "stalled") && (
        <AlertTriangle className="w-2.5 h-2.5"/>
      )}
      {slaStatus === "pending"  ? "SLA pending" :
       slaStatus === "rejected" ? "SLA rejected" :
       movement === "no_movement" ? "No movement" :
       movement === "stalled"     ? "Stalled" :
       status || "pending"}
    </span>
  );
}
